import { useState } from "react";
import { readLayout } from "@/components/leads-table";
import { Placeholder } from "@/components/page-states";
import { useT } from "@shared/client/locale";
import { StatGroup } from "@shared/components/blocks";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@shared/components/ui/table";
import { WINDOW_DAYS } from "@shared/lib/dashboard-metrics";
import { cn } from "@shared/lib/utils";

/**
 * Les squelettes des écrans produit : la structure de chaque écran, ses
 * cadres et ses filets, avec des blocs gris à la place des données (charte
 * §5.1 : « Skeleton dès que la structure du résultat est connue »). Les
 * libellés fixes (titres de section) restent écrits : ils ne dépendent de
 * rien. Chaque squelette suit les classes de l'écran qu'il annonce, pour que
 * rien ne saute à l'arrivée des données — à retoucher avec lui.
 */

const rows = (count: number) => Array.from({ length: count }, (_, index) => index);

/** En-tête éditorial (`PageHeader`) : contexte, titre serif, chapô. */
function PageHeaderSkeleton({ eyebrow, lede = false }: { eyebrow?: string; lede?: boolean }) {
  return (
    <header className="mb-9">
      <div className="max-w-[680px]">
        {eyebrow && <span className="text-[13px] text-muted-foreground">{eyebrow}</span>}
        <Placeholder className="mt-2 mb-2.5 h-[34px] w-[min(360px,80%)]" />
        {lede && (
          <div className="grid gap-2 py-1">
            <Placeholder className="h-3.5 w-full" />
            <Placeholder className="h-3.5 w-3/4" />
          </div>
        )}
      </div>
    </header>
  );
}

/** Deux lignes empilées : un nom, puis sa précision. */
function TwoLines({ first, second, className }: { first: string; second: string; className?: string }) {
  return (
    <span className={cn("grid min-w-0 gap-2", className)}>
      <Placeholder className={cn("h-3.5", first)} />
      <Placeholder className={cn("h-3", second)} />
    </span>
  );
}

/** Vue d'ensemble : carte de veille, bande de mesures, derniers passages et pipeline. */
export function DashboardSkeleton() {
  const copy = useT().common.skeletons;
  return (
    <div className="grid gap-12">
      <div className="overflow-hidden rounded-lg border">
        <div className="flex flex-wrap items-start gap-x-6 gap-y-4 px-6 pt-5.5 pb-5">
          <div className="mr-auto flex min-w-0 items-center gap-3.5">
            <Placeholder className="size-10 flex-none" />
            <TwoLines first="w-44" second="w-60 max-sm:w-40" />
          </div>
          <div className="flex items-center gap-4">
            <Placeholder className="h-3 w-28" />
            <Placeholder className="h-[1.15rem] w-8 rounded-full" />
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-x-10 gap-y-4 border-t px-6 py-5">
          {rows(3).map((index) => (
            <span className="grid gap-2" key={index}>
              <Placeholder className="h-6 w-10" />
              <Placeholder className="h-3 w-24" />
            </span>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2.5 border-t px-6 py-4">
          <Placeholder className="h-9 w-44" />
          <Placeholder className="h-9 w-36" />
        </div>
      </div>

      <section className="grid gap-3">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h2 className="m-0 text-[14px] font-semibold">{copy.activity(WINDOW_DAYS)}</h2>
          <span className="text-[12px] text-subtle-foreground">{copy.comparedWith(WINDOW_DAYS)}</span>
        </div>
        <StatGroup columns={3}>
          {rows(3).map((index) => (
            <div className="min-w-0 border-r border-b px-5 py-4" key={index}>
              <Placeholder className="h-3 w-24" />
              <Placeholder className="mt-2.5 h-6 w-12" />
              <Placeholder className="mt-2.5 h-3 w-32 max-w-full" />
            </div>
          ))}
        </StatGroup>
      </section>

      <div className="grid items-start gap-x-12 gap-y-10 lg:grid-cols-[1.4fr_1fr]">
        <section className="grid gap-3">
          <h2 className="m-0 text-[14px] font-semibold">{copy.recentPasses}</h2>
          <ul className="m-0 list-none border-y p-0">
            {rows(4).map((index) => (
              <li className="flex items-center justify-between gap-4 border-t py-3 first:border-t-0" key={index}>
                <TwoLines first="w-40" second="w-28" />
                <span className="flex flex-none items-center gap-5">
                  <Placeholder className="h-3 w-16" />
                  <Placeholder className="h-3 w-[72px] max-sm:hidden" />
                </span>
              </li>
            ))}
          </ul>
        </section>
        <section className="grid gap-3">
          <h2 className="m-0 text-[14px] font-semibold">{copy.pipeline}</h2>
          <div className="grid gap-4 border-y py-4">
            <Placeholder className="h-2.5 w-full rounded-[3px]" />
            <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
              {rows(4).map((index) => (
                <Placeholder className="h-3 w-full" key={index} />
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

/** Leads : ligne méta, barre d'outils, puis cartes ou table selon la présentation choisie. */
export function LeadsSkeleton() {
  const [layout] = useState(readLayout);
  const copy = useT().common.skeletons;
  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4 text-[12px] text-muted-foreground">
        <Placeholder className="h-3 w-48" />
        <span className="text-[12px] text-muted-foreground/80">{copy.leadsSource}</span>
      </div>
      <div className="overflow-hidden rounded-lg border">
        <div className="flex flex-wrap items-center gap-3 border-b px-4 py-3">
          <Placeholder className="h-9 min-w-[220px] flex-1 max-sm:min-w-full" />
          <Placeholder className="h-8 w-[508px] max-w-full" />
          <div className="flex items-center gap-2">
            <Placeholder className="h-8 w-[72px]" />
            <Placeholder className="h-8 w-[90px]" />
          </div>
        </div>
        {layout === "table" ? (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-10 pl-3 max-sm:pl-1" />
                <TableHead className="pl-1">{copy.organization}</TableHead>
                <TableHead className="max-md:hidden">{copy.signal}</TableHead>
                <TableHead className="max-lg:hidden">{copy.contact}</TableHead>
                <TableHead className="w-16 text-center">{copy.score}</TableHead>
                <TableHead className="w-28 max-sm:hidden">{copy.status}</TableHead>
                <TableHead className="w-12 pr-3" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows(8).map((index) => (
                <TableRow className="hover:bg-transparent" key={index}>
                  <TableCell className="pl-3 max-sm:pl-1">
                    <Placeholder className="size-4" />
                  </TableCell>
                  <TableCell className="py-3 pl-1">
                    <span className="flex items-center gap-3">
                      <Placeholder className="size-9 flex-none" />
                      <TwoLines first="w-36" second="w-24" />
                    </span>
                  </TableCell>
                  <TableCell className="max-md:hidden">
                    <TwoLines first="w-64" second="w-28" />
                  </TableCell>
                  <TableCell className="max-lg:hidden">
                    <span className="flex items-center gap-2.5">
                      <Placeholder className="size-7 flex-none rounded-full" />
                      <TwoLines first="w-24" second="w-20" />
                    </span>
                  </TableCell>
                  <TableCell>
                    <Placeholder className="mx-auto size-9 rounded-full" />
                  </TableCell>
                  <TableCell className="max-sm:hidden">
                    <Placeholder className="h-3 w-16" />
                  </TableCell>
                  <TableCell className="pr-3" />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="grid gap-3 p-4 md:grid-cols-2 xl:grid-cols-3">
            {rows(6).map((index) => (
              <div className="flex flex-col gap-4 rounded-lg border p-4" key={index}>
                <div className="flex items-start justify-between gap-3">
                  <span className="flex min-w-0 items-center gap-3">
                    <Placeholder className="size-10 flex-none" />
                    <TwoLines first="w-32" second="w-24" />
                  </span>
                  <Placeholder className="size-10 flex-none rounded-full" />
                </div>
                <div className="grid gap-2">
                  <Placeholder className="h-3 w-28" />
                  <Placeholder className="h-3.5 w-full" />
                  <Placeholder className="h-3.5 w-5/6" />
                </div>
                <div className="mt-auto flex items-center justify-between gap-3 border-t pt-3">
                  <span className="flex items-center gap-2">
                    <Placeholder className="size-6 rounded-full" />
                    <Placeholder className="h-3 w-20" />
                  </span>
                  <Placeholder className="h-3 w-14" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

/** Passages : en-tête, puis la table de l'historique. */
export function ReportsSkeleton() {
  const copy = useT().common.skeletons;
  return (
    <>
      <PageHeaderSkeleton eyebrow={copy.history} lede />
      <div className="overflow-hidden rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="pl-5 max-sm:pl-4">{copy.pass}</TableHead>
              <TableHead className="max-sm:hidden">{copy.startedAt}</TableHead>
              <TableHead className="max-sm:hidden">{copy.state}</TableHead>
              <TableHead className="pr-5 text-right max-sm:pr-3">
                <span className="max-sm:sr-only">{copy.report}</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows(5).map((index) => (
              <TableRow className="hover:bg-transparent" key={index}>
                <TableCell className="pl-5 max-sm:pl-4">
                  <TwoLines className="py-0.5" first="w-44" second="w-60 max-sm:w-40" />
                </TableCell>
                <TableCell className="max-sm:hidden">
                  <Placeholder className="h-3 w-20" />
                </TableCell>
                <TableCell className="max-sm:hidden">
                  <Placeholder className="h-3 w-28" />
                </TableCell>
                <TableCell className="pr-5 max-sm:pr-3">
                  <Placeholder className="ml-auto h-8 w-24 max-sm:w-8" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

/** Bloc de squelette sur la couverture noire du rapport. */
const ON_DARK = "bg-background/15";

/**
 * Rapport d'un passage : couverture (noire, comme celle du rapport : son
 * arrivée ne renverse pas le contraste), mesures, stratégie de recherche.
 */
export function ReportSkeleton() {
  return (
    <section className="mt-12 mb-8">
      <div className="grid items-end gap-7 rounded-t-[22px] bg-foreground p-10 md:grid-cols-[1fr_auto] max-sm:rounded-t-2xl max-sm:p-6">
        <div className="grid gap-4">
          <Placeholder className={cn(ON_DARK, "h-3 w-32")} />
          <Placeholder className={cn(ON_DARK, "mt-1 h-12 w-2/3 max-sm:h-9")} />
          <div className="grid max-w-[780px] gap-2">
            <Placeholder className={cn(ON_DARK, "h-3.5 w-full")} />
            <Placeholder className={cn(ON_DARK, "h-3.5 w-4/5")} />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {rows(3).map((index) => (
            <Placeholder className={cn(ON_DARK, "h-8 w-28")} key={index} />
          ))}
        </div>
      </div>
      <div className="grid border border-t-0 border-foreground md:grid-cols-[0.5fr_0.5fr_2fr]">
        {rows(3).map((index) => (
          <div
            className={cn(
              "flex min-h-[135px] flex-col justify-between gap-3 p-6 max-sm:min-h-0 max-sm:p-5",
              index > 0 && "border-t md:border-t-0 md:border-l",
            )}
            key={index}
          >
            <Placeholder className="h-3 w-28" />
            <Placeholder className={index === 2 ? "h-4 w-3/4" : "h-10 w-14"} />
          </div>
        ))}
      </div>
      <div className="mt-4.5 grid overflow-hidden rounded-lg border lg:grid-cols-[0.65fr_1.35fr]">
        <div className="grid content-start gap-3 border-b p-7 lg:border-r lg:border-b-0 max-sm:p-5">
          <Placeholder className="h-3 w-36" />
          <Placeholder className="mt-2 h-8 w-56 max-w-full" />
          <Placeholder className="h-3 w-full" />
          <Placeholder className="h-3 w-5/6" />
        </div>
        <div className="grid sm:grid-cols-2">
          {rows(4).map((index) => (
            <div className="grid content-start gap-2 border-b p-5 sm:odd:border-r" key={index}>
              <Placeholder className="h-3.5 w-32" />
              <Placeholder className="h-3 w-full" />
              <Placeholder className="h-3 w-2/3" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/** Mes offres : la fiche de l'offre consultée, dépliée, puis l'ajout d'une offre. */
export function OffersSkeleton() {
  const copy = useT().common.skeletons;
  return (
    <div className="max-w-[920px]">
      <PageHeaderSkeleton />
      <div className="grid gap-4">
        <div className="rounded-lg border">
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 p-5 max-sm:p-4">
            <span className="flex min-w-0 items-center gap-3">
              <Placeholder className="size-9 flex-none rounded-lg" />
              <TwoLines first="h-5 w-40" second="w-28" />
            </span>
            <span className="flex items-center gap-3">
              <Placeholder className="h-3 w-32" />
              <Placeholder className="h-[22px] w-16" />
            </span>
          </div>
          <div className="grid gap-3 border-t px-5 py-4 max-sm:px-4">
            <h3 className="m-0 text-[14px] font-semibold">{copy.targeting}</h3>
            <Placeholder className="h-3 w-full" />
            <Placeholder className="h-3 w-5/6" />
            <Placeholder className="h-3 w-2/3" />
          </div>
          <div className="grid gap-3 border-t p-5 max-sm:p-4">
            <h3 className="m-0 text-[14px] font-semibold">{copy.monitoring}</h3>
            <Placeholder className="h-9 w-full max-w-[420px]" />
          </div>
        </div>
        <div className="grid gap-4 rounded-lg border p-6 max-sm:px-4">
          <TwoLines first="w-36" second="w-72 max-w-full" />
          <div className="flex flex-wrap items-end gap-4">
            <Placeholder className="h-10 min-w-[260px] flex-1" />
            <Placeholder className="h-10 w-36" />
          </div>
        </div>
      </div>
    </div>
  );
}

/** Abonnement (Paramètres) : le plan en vigueur et ses mesures, puis le comparatif. */
export function BillingSkeleton() {
  return (
    <div className="grid gap-5">
      <div className="rounded-lg border">
        <div className="grid gap-2.5 p-5 max-sm:p-4">
          <Placeholder className="h-3 w-20" />
          <Placeholder className="h-[30px] w-40" />
          <Placeholder className="h-3 w-48" />
        </div>
        <div className="grid grid-cols-3 border-t">
          {rows(3).map((index) => (
            <div className="grid content-start gap-2.5 border-r p-4 last:border-r-0 max-sm:p-3" key={index}>
              <Placeholder className="h-3 w-20 max-w-full" />
              <Placeholder className="h-5 w-16 max-w-full" />
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Placeholder className="h-3.5 w-32" />
        <Placeholder className="h-8 w-48" />
      </div>
      <div className="overflow-hidden rounded-lg border">
        <div className="grid grid-cols-[34%_1fr_1fr_1fr] max-sm:grid-cols-[27%_1fr_1fr_1fr]">
          <span />
          {rows(3).map((index) => (
            <div className="grid justify-items-center gap-2 border-l p-4 max-sm:px-1" key={index}>
              <Placeholder className="h-3.5 w-12" />
              <Placeholder className="h-6 w-16 max-w-full" />
            </div>
          ))}
          {rows(4).map((row) => (
            <div className="contents" key={row}>
              <div className="border-t px-4 py-3 max-sm:px-2">
                <Placeholder className="h-3 w-28 max-w-full" />
              </div>
              {rows(3).map((index) => (
                <div className="grid place-items-center border-t border-l px-2 py-3" key={index}>
                  <Placeholder className="h-3 w-10" />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Nouveau mot de passe, le temps de vérifier le lien reçu par e-mail : le formulaire d'`AuthForm`. */
export function ResetPasswordSkeleton() {
  return (
    <div className="w-[420px] max-w-full">
      <div className="mb-6 grid gap-2">
        <Placeholder className="h-3 w-32" />
        <Placeholder className="h-[34px] w-64 max-w-full" />
        <Placeholder className="h-3.5 w-full" />
      </div>
      <div className="grid gap-4">
        {rows(2).map((index) => (
          <div className="grid gap-2" key={index}>
            <Placeholder className="h-3.5 w-40" />
            <Placeholder className="h-11 w-full" />
          </div>
        ))}
        <Placeholder className="h-10 w-full" />
      </div>
    </div>
  );
}
